import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type ContractCivilKind } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Управління видами договорів по ЦК (tenant-scoped, TD-CONTRACT-FIELDS 2026-06-06).
 * Дефолтні 12 видів (is_seed) — не можна видалити. Юзер створює свої.
 * При імпорті з 1С вид договору мапиться на цей довідник (get-or-create по name).
 */
export default function ContractCivilKindsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');

  const list = useQuery({
    queryKey: ['contract-civil-kinds'],
    queryFn: refs.contractCivilKinds.list,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['contract-civil-kinds'] });

  const create = useMutation({
    mutationFn: () => refs.contractCivilKinds.create({ name: newName.trim() }),
    onSuccess: () => { setNewName(''); invalidate(); },
  });
  const update = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      refs.contractCivilKinds.update(id, { name }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.contractCivilKinds.remove(id),
    onSuccess: invalidate,
  });

  const rows = list.data ?? [];

  const cols = useMemo<ColumnDef<ContractCivilKind>[]>(() => [
    {
      key: 'name', header: 'Назва',
      sortValue: (k) => k.name.toLowerCase(),
      cell: (k) => (
        <>
          {k.is_seed && <span title="Дефолтний вид">🔒 </span>}
          {k.is_seed ? (
            k.name
          ) : (
            <input
              defaultValue={k.name}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== k.name) update.mutate({ id: k.id, name: v });
              }}
              style={{ width: '90%' }}
            />
          )}
        </>
      ),
    },
    {
      key: 'actions', header: '', width: 90, align: 'center',
      cell: (k) =>
        !k.is_seed ? (
          <button
            className="danger"
            title="Видалити вид"
            onClick={() =>
              confirm(`Видалити вид «${k.name}»?`).then((ok) => {
                if (ok) remove.mutate(k.id);
              })
            }
          >
            ✕
          </button>
        ) : null,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [update, remove, confirm]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Види договорів (за ЦК)</h2>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Вид договору за Цивільним кодексом (купівлі-продажу, поставки, послуг
        тощо) — довідкове поле картки договору. Дефолтні види позначені 🔒 —
        їх не можна видалити. Створюйте власні види за потреби.
      </p>

      {/* Створення нового виду */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '1rem 0' }}>
        <input
          placeholder="Назва нового виду"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <button
          className="primary"
          disabled={!newName.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          + Додати вид
        </button>
      </div>

      <div style={{ maxWidth: 720 }}>
        <DataTable<ContractCivilKind>
          rows={rows}
          columns={cols}
          rowKey={(k) => k.id}
          loading={list.isLoading}
          emptyText="Немає видів договорів."
          rowMuted={(k) => (k.deletion_mark ? 0.4 : undefined)}
          initialSortKey="name"
        />
      </div>
    </>
  );
}
